class CaseStudySlider {
  constructor(slider) {
    this.slider = slider;
    this.list = slider.querySelector('ul');
    this.prevBtn = slider.querySelector('.slider-arrow:first-child');
    this.nextBtn = slider.querySelector('.slider-arrow:last-child');
    this.slides = this.list ? Array.from(this.list.children) : [];
    this.currentIndex = 0;
    this.isScrolling = false;
    this.scrollEndTimer = null;

    // Initialize if we have slides
    if (this.slides.length > 0) {
      this.init();
    }
  }

  init() {
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.scrollTo('prev'));
    } else {
      console.warn('CaseStudySlider: Previous button not found.');
    }

    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.scrollTo('next'));
    } else {
      console.warn('CaseStudySlider: Next button not found.');
    }

    if (this.list) {
      this.list.addEventListener('scroll', this.handleScroll.bind(this));
    } else {
      console.error('CaseStudySlider: Slide list element not found. Slider cannot function.');
      return; // Critical element missing
    }

    // Handle window resize
    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => this.handleResize(), 100);
    };
    window.addEventListener('resize', handleResize);

    // Initial setup
    this.centerSlider();
    this.updateButtonStates(); // Call after potential centering and list is definitely available
  }

  centerSlider() {
    // Initial centering on load
    if (!this.list || this.slides.length === 0) return;

    const totalWidth = this.list.scrollWidth;
    const visibleWidth = this.list.clientWidth;

    if (totalWidth > visibleWidth) {
      // Only center if content overflows
      const scrollToPosition = (totalWidth - visibleWidth) / 2;
      this.list.scrollLeft = scrollToPosition;
    } else {
      // If content doesn't overflow, ensure it's scrolled to the start.
      this.list.scrollLeft = 0;
    }

    // Update button states after centering, slight delay for layout stability
    setTimeout(() => this.updateButtonStates(), 100);
  }

  centerGivenSlide(slideElement, smooth = true) {
    if (!slideElement || !this.list) return;

    const slideCenter = slideElement.offsetLeft + slideElement.offsetWidth / 2;
    const viewportCenter = this.list.clientWidth / 2;
    const newScrollLeft = slideCenter - viewportCenter;

    this.list.scrollTo({
      left: newScrollLeft,
      behavior: smooth ? 'smooth' : 'auto',
    });
  }

  scrollTo(direction) {
    if (!this.list || this.isScrolling || this.slides.length === 0) return;

    let targetIndex;
    if (direction === 'next') {
      targetIndex = Math.min(this.currentIndex + 1, this.slides.length - 1);
    } else {
      // 'prev'
      targetIndex = Math.max(this.currentIndex - 1, 0);
    }

    // If trying to scroll beyond limits and already at the edge, or no change in index
    if (
      targetIndex === this.currentIndex &&
      ((direction === 'next' && this.currentIndex === this.slides.length - 1) ||
        (direction === 'prev' && this.currentIndex === 0))
    ) {
      return; // Buttons should be disabled, but this is a safeguard
    }

    const targetSlide = this.slides[targetIndex];

    if (!targetSlide) return;

    this.isScrolling = true;
    this.centerGivenSlide(targetSlide, true); // true for smooth scroll

    this.currentIndex = targetIndex;

    // Update button states after scroll animation likely completes
    setTimeout(() => {
      this.isScrolling = false;
      this.updateButtonStates();
    }, 300); // Adjust timeout to match smooth scroll duration
  }

  updateButtonStates() {
    if (!this.list) return; // List is essential

    const { scrollLeft, scrollWidth: sliderWidth, clientWidth } = this.list;

    if (sliderWidth <= clientWidth) {
      // All slides are visible or fit within the container
      if (this.prevBtn) {
        this.prevBtn.classList.add('hidden');
        this.prevBtn.disabled = true;
      }
      if (this.nextBtn) {
        this.nextBtn.classList.add('hidden');
        this.nextBtn.disabled = true;
      }
      this.list.style.justifyContent = 'center';
    } else {
      // Slides overflow, arrows needed
      const isAtStart = scrollLeft <= 10;
      const isAtEnd = scrollLeft + clientWidth >= sliderWidth - 10;

      if (this.prevBtn) {
        this.prevBtn.classList.remove('hidden');
        this.prevBtn.disabled = isAtStart;
      }
      if (this.nextBtn) {
        this.nextBtn.classList.remove('hidden');
        this.nextBtn.disabled = isAtEnd;
      }
      this.list.style.justifyContent = 'start'; // Or 'start' if preferred
    }
  }

  handleScroll() {
    if (this.scrollEndTimer) {
      clearTimeout(this.scrollEndTimer);
    }

    // Debounce the scroll event
    this.scrollEndTimer = setTimeout(() => {
      this.updateButtonStates();
    }, 100);
  }

  handleResize() {
    if (!this.slides.length || !this.list) return;

    // Re-center the current slide as viewport dimensions might have changed
    const currentSlide = this.slides[this.currentIndex];
    if (currentSlide) {
      this.centerGivenSlide(currentSlide, false); // false for immediate scroll
    }

    this.updateButtonStates();
  }
}

// Initialize sliders when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  const sliders = document.querySelectorAll('.case-study-slider');
  sliders.forEach((slider) => new CaseStudySlider(slider));
});
